import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";

const FAQS = [
  {
    q: "How does Trainova work?",
    a: "Trainova is a swipe-based fitness app that allows users to find and match with gym partners based on their fitness goals and preferences. Simply create a profile, swipe through potential matches, and connect with like-minded individuals to motivate each other.",
  },
  {
    q: "Can I find partners for specific workouts?",
    a: "Absolutely! You can specify your preferred workout types, whether it's weightlifting, yoga, or running, and match with partners who share those interests.",
  },
  {
    q: "What should I do if I encounter issues with the app?",
    a: "If you experience any issues, please contact our support team through the app, and we'll be happy to assist you promptly.",
  },
  {
    q: "How do I delete my Trainova account?",
    a: "Open the Trainova app, go to Side Bar > Settings > Delete Account, and confirm your request. If you can't access your account, email support@trainovaapp.com for assistance.",
  },
  {
    q: "What happens when I delete my account?",
    a: "Deleting your account permanently removes your profile, photos, matches, messages, workout preferences, and associated personal information from Trainova.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="faq-page" className="bg-[#0A0A0A]">
      <PageHero
        eyebrow="TRAIN BETTER. TOGETHER"
        title="Frequently asked"
        accent="questions"
        subtitle="Everything you need to know about matching, training and managing your Trainova account."
      />

      <section className="mx-auto max-w-[1100px] px-6 py-20 md:px-12 md:py-28">
        <div className="border-t border-white/10">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-b border-white/10" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-question-${i}`}
                    className="group flex w-full items-center justify-between gap-6 py-7 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-start gap-5">
                      <span className="font-display text-2xl text-teal/60 md:text-3xl">
                        0{i + 1}
                      </span>
                      <span className="font-condensed text-2xl tracking-wide transition-colors duration-200 group-hover:text-teal md:text-3xl">
                        {item.q}
                      </span>
                    </span>
                    <span
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center border transition-colors duration-300 ${
                        isOpen ? "border-teal bg-teal text-black" : "border-white/20 text-white"
                      }`}
                    >
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p
                          data-testid={`faq-answer-${i}`}
                          className="max-w-3xl pb-8 pl-0 text-base leading-relaxed text-zinc-400 md:pl-[3.75rem] md:text-lg"
                        >
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-col items-start gap-6 border border-white/10 bg-[#101010] p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <h2 className="font-condensed text-3xl tracking-wide md:text-4xl">Still have questions?</h2>
              <p className="mt-2 text-zinc-400">Our team is here to help. Reach out any time.</p>
            </div>
            <a
              href="#contact"
              data-testid="faq-contact-btn"
              className="inline-flex bg-teal px-8 py-4 font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.03]"
            >
              Get in Touch
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
